import { estimatedTotal } from "./caseValue";
import type { Case } from "./useRealtimeCases";
import { formatParisDate, formatParisTime } from "./parisDateTime";

/** Rythme actuel des relances devis : 4 e-mails J+2 / J+4 / J+6 / J+8 (Paul, 04/11 : on garde ce rythme). */
export const REMINDER_STEPS = 4;
/** Seuil unique « gros devis » : 1 500 € TTC (Paul, 04/11), soit 1 250 € HT sur le budget du dossier. */
export const BIG_QUOTE_TTC = 1500;
const TVA = 1.2;

export type ReminderCycle = {
  id: string;
  case_id: string;
  status: "active" | "waiting_staff" | "paused" | "completed" | "unsubscribed" | "rejected" | "error";
  current_step: number | null;
  next_step: number | null;
  next_reminder_at: string | null;
  last_client_response_at: string | null;
  last_error: string | null;
  stopped_reason: string | null;
  paused_at: string | null;
  completed_at: string | null;
  unsubscribed_at: string | null;
  rejected_at: string | null;
  updated_at: string;
  created_at: string;
};

export type ReminderTone = "running" | "you" | "stopped" | "error";
export type Segment = "sent" | "next" | "failed" | "empty";

export type ReminderProgressView = {
  tone: ReminderTone;
  label: string;
  detail: string | null;
  sent: number;
  segments: Segment[];
  callRecommended: boolean;
};

const STOP_REASONS: Record<string, string> = {
  signed: "devis signé",
  max_steps: "pas de réponse",
  staff_email: "e-mail envoyé par l’équipe",
  staff_call: "appel noté",
  case_moved: "carte déplacée",
  manual: "arrêtées à la main",
  unsubscribed: "le client ne veut plus de relances",
  rejected: "devis refusé",
};

function when(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const day = formatParisDate(iso, { weekday: "short", day: "2-digit", month: "2-digit" });
  return `${day} à ${formatParisTime(iso)}`;
}

function shortDate(iso: string | null): string {
  if (!iso) return "";
  return formatParisDate(iso, { day: "2-digit", month: "2-digit" });
}

/** Garde le cycle le plus récent par dossier. */
export function latestCycleByCase(cycles: ReminderCycle[]): Map<string, ReminderCycle> {
  const map = new Map<string, ReminderCycle>();
  for (const cycle of cycles) {
    const prev = map.get(cycle.case_id);
    if (!prev || cycle.created_at > prev.created_at) map.set(cycle.case_id, cycle);
  }
  return map;
}

export function isBigQuote(c: Pick<Case, "budget" | "budget_per_person" | "participants" | "pax">): boolean {
  const total = estimatedTotal(c);
  return total !== null && total * TVA >= BIG_QUOTE_TTC;
}

/** Ce que la carte Kanban affiche pour les relances d’un dossier. */
type ProgressCase = Pick<Case, "status" | "budget" | "budget_per_person" | "participants" | "pax">;
const SIGNED_STATUSES = new Set(["Signé", "Acompte reçu", "Versement complet"]);

/**
 * Ce que la carte Kanban (et la colonne Relances de la fiche) affiche pour les relances d’un dossier.
 * Par défaut, seulement pour « Devis envoyé » et « Signé » ; `anyStatus` couvre tous les dossiers suivis.
 */
export function reminderProgress(
  c: ProgressCase,
  cycle: ReminderCycle | undefined,
  now = new Date(),
  options: { anyStatus?: boolean } = {}
): ReminderProgressView | null {
  if (!cycle) return null;
  if (!options.anyStatus && c.status !== "Devis envoyé" && c.status !== "Signé") return null;

  const sent = Math.min(Math.max(Number(cycle.current_step) || 0, 0), REMINDER_STEPS);
  const segments: Segment[] = Array.from({ length: REMINDER_STEPS }, (_, i) => (i < sent ? "sent" : "empty"));
  const nextIndex = sent; // la prochaine relance = segment suivant
  const callRecommended = c.status === "Devis envoyé" && isBigQuote(c);
  const base = { sent, segments, callRecommended };

  // Le robot ne relance que les dossiers en « Devis envoyé » : une carte ailleurs = relances arrêtées.
  if (SIGNED_STATUSES.has(c.status)) {
    return { ...base, callRecommended: false, tone: "stopped", label: "Arrêtées · devis signé", detail: cycle.completed_at ? `Signé le ${shortDate(cycle.completed_at)}` : null };
  }

  const stillRunning = ["active", "waiting_staff", "error"].includes(cycle.status);
  if (c.status !== "Devis envoyé" && stillRunning) {
    return { ...base, callRecommended: false, tone: "stopped", label: "Arrêtées · carte déplacée", detail: `Dossier en « ${c.status} »` };
  }

  switch (cycle.status) {
    case "active": {
      if (sent >= REMINDER_STEPS) return { ...base, tone: "stopped", label: "Terminées · pas de réponse", detail: null };
      segments[nextIndex] = "next";
      const due = cycle.next_reminder_at;
      const late = due ? new Date(due).getTime() < now.getTime() : false;
      return {
        ...base,
        tone: "running",
        label: "Relances en cours",
        detail: due ? (late ? `Relance ${nextIndex + 1} en attente d’envoi` : `Relance ${nextIndex + 1} ${when(due)}`) : null,
      };
    }
    case "waiting_staff":
      return {
        ...base,
        tone: "you",
        label: "Arrêtées · le client a répondu",
        detail: cycle.last_client_response_at ? `Réponse du ${when(cycle.last_client_response_at)} · à vous de répondre` : "À vous de répondre",
      };
    case "paused": {
      // Arrêt par un humain (e-mail, appel, carte déplacée) ou pause manuelle : la raison reste visible.
      const reason = cycle.stopped_reason ? STOP_REASONS[cycle.stopped_reason] ?? null : null;
      return {
        ...base,
        tone: "stopped",
        label: `Arrêtées · ${reason ?? "en pause"}`,
        detail: cycle.paused_at ? `Le ${shortDate(cycle.paused_at)} · reprise possible dans la fiche (Relances)` : null,
      };
    }
    case "unsubscribed":
      return { ...base, tone: "stopped", label: "Arrêtées · le client ne veut plus de relances", detail: cycle.unsubscribed_at ? `Le ${shortDate(cycle.unsubscribed_at)}` : null };
    case "rejected":
      return { ...base, tone: "stopped", label: "Arrêtées · devis refusé", detail: cycle.rejected_at ? `Le ${shortDate(cycle.rejected_at)}` : null };
    case "error":
      if (nextIndex < REMINDER_STEPS) segments[nextIndex] = "failed";
      return { ...base, tone: "error", label: `Relance ${Math.min(nextIndex + 1, REMINDER_STEPS)} non partie`, detail: cycle.last_error || "Erreur d’envoi" };
    case "completed":
    default: {
      const reason = cycle.stopped_reason ? STOP_REASONS[cycle.stopped_reason] ?? cycle.stopped_reason : null;
      const done = cycle.stopped_reason === "max_steps" || sent >= REMINDER_STEPS;
      return {
        ...base,
        tone: "stopped",
        label: done ? "Terminées · pas de réponse" : `Arrêtées${reason ? ` · ${reason}` : ""}`,
        detail: cycle.completed_at ? `Le ${shortDate(cycle.completed_at)}` : null,
      };
    }
  }
}
